import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Avatar, Popover, Tooltip, useToast } from '@vd/ui';
import { api } from '../../lib/api';
import { AssigneePicker } from '../ticket/AssigneePicker';
import { NarrationAssignmentNotes, narrationAssignmentsKey } from '../ticket/NarrationAssignmentNotes';
import styles from './ReviewAssignees.module.css';

/**
 * Who has a walkthrough ticket, on the page built for reviewing the walkthrough
 * (TOADO-474).
 *
 * This page is where a person reads what the walkthrough produced, including who
 * its narration assigned or offered for each ticket, and
 * without a control here, assigning meant opening every ticket. Assigning saves at
 * once, like the ticket page, rather than waiting for the editor's Save: it is a
 * separate write, and holding it back behind the text fields would make a
 * change of assignee look unsaved when it is not.
 */
export function ReviewAssignees({
  ticketId,
  projectId,
  companyId,
  assignees,
}: {
  ticketId: string;
  projectId: string;
  companyId: string;
  assignees: readonly string[];
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const members = useQuery({
    queryKey: ['members', companyId],
    queryFn: () => api.listMembers(companyId),
  });
  const everyone = (members.data ?? []).map((member) => member.user);
  const assigned = everyone.filter((user) => assignees.includes(user.id));

  const assign = useMutation({
    mutationFn: (userIds: string[]) => api.assignTicket(ticketId, userIds, 'replace'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      // Taking somebody off by hand retires the walkthrough's note about them.
      void queryClient.invalidateQueries({ queryKey: narrationAssignmentsKey(ticketId) });
      void queryClient.invalidateQueries({ queryKey: ['tickets', projectId] });
    },
    onError: (error: unknown) => {
      toast.push({
        variant: 'danger',
        title: 'Could not change who has this ticket',
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });

  return (
    <div className={styles.assignees}>
      <div className={styles.row}>
        <span className={styles.label}>Assigned</span>
        <div className={styles.list}>
          {assigned.map((user) => (
            <Tooltip key={user.id} content={user.name}>
              <Avatar name={user.name} src={user.avatarUrl} size="sm" />
            </Tooltip>
          ))}
          <button
            type="button"
            className={styles.assignButton}
            aria-label={assigned.length === 0 ? 'Assign' : 'Change who is assigned'}
            disabled={assign.isPending || !members.data}
            onClick={(event) => setAnchor(event.currentTarget.getBoundingClientRect())}
          >
            <Plus size={12} />
            {assigned.length === 0 ? 'Assign' : null}
          </button>
        </div>
      </div>
      <NarrationAssignmentNotes ticketId={ticketId} projectId={projectId} />
      <Popover
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchor={anchor}
        placement="bottom-start"
        width={280}
      >
        <AssigneePicker
          allMembers={everyone}
          selected={[...assignees]}
          onDone={(userIds) => {
            assign.mutate(userIds);
            setAnchor(null);
          }}
          onCancel={() => setAnchor(null)}
        />
      </Popover>
    </div>
  );
}
